import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import Load from './Load';
import Pagination from './Pagination';
import MatchCard from './MatchCard';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import {
  PARTIDAS_COM_FILTROS,
  PartidasComFiltrosData,
  PartidasComFiltrosVars,
  PartidaResumo,
} from '../services/partidaService';

interface AtletaJogosTabProps {
  atletaId: string;
}

const PAGE_SIZE = 6;

const statusOptions = [
  { value: 'AGENDADA', label: 'Agendada' },
  { value: 'EM_ANDAMENTO', label: 'Em andamento' },
  { value: 'FINALIZADA', label: 'Finalizada' },
  { value: 'CANCELADA', label: 'Cancelada' },
];

const AtletaJogosTab: React.FC<AtletaJogosTabProps> = ({ atletaId }) => {
  const [page, setPage] = useState(0);
  const [filtroTorneioId, setFiltroTorneioId] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');

  const filtros = useMemo(() => ({
    atletaId,
    torneioId: filtroTorneioId || null,
    status: filtroStatus || null,
    dataInicio: filtroDataInicio ? filtroDataInicio + 'T00:00:00Z' : null,
    dataFim: filtroDataFim ? filtroDataFim + 'T23:59:59Z' : null,
  }), [atletaId, filtroTorneioId, filtroStatus, filtroDataInicio, filtroDataFim]);

  const { data, loading, error } = useQuery<
    PartidasComFiltrosData,
    PartidasComFiltrosVars
  >(PARTIDAS_COM_FILTROS, {
    variables: { filtros },
    skip: !atletaId,
  });

  const allItems = useMemo(() => {
    const partidas = data?.partidasComFiltros ?? [];
    return [...partidas]
      .sort((a, b) => {
        const da = a.data ? new Date(a.data).getTime() : 0;
        const db = b.data ? new Date(b.data).getTime() : 0;
        return db - da;
      })
      .map((p) => ({
        ...p,
        categoriaTorneio: {
          id: p.categoriaTorneio.id,
          nome: p.categoriaTorneio.nome,
          torneio: p.categoriaTorneio.torneio ?? { id: '', nome: '', avatarUrl: null },
        },
      })) as PartidaResumo[];
  }, [data]);

  const torneiosUnicos = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of allItems) {
      const t = item.categoriaTorneio.torneio;
      if (t?.id && t?.nome) {
        map.set(t.id, t.nome);
      }
    }
    return Array.from(map.entries()).map(([id, nome]) => ({ id, nome }));
  }, [allItems]);

  const totalPages = Math.ceil(allItems.length / PAGE_SIZE);
  const items = allItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const hasDateError = !!(filtroDataInicio && filtroDataFim && filtroDataInicio > filtroDataFim);
  const temFiltroAtivo = !!(filtroTorneioId || filtroStatus || filtroDataInicio || filtroDataFim);

  const handleLimparFiltros = () => {
    setFiltroTorneioId('');
    setFiltroStatus('');
    setFiltroDataInicio('');
    setFiltroDataFim('');
    setPage(0);
  };

  const handleFilterChange = <T,>(setter: React.Dispatch<React.SetStateAction<T>>, value: T) => {
    setter(value);
    setPage(0);
  };

  if (loading) {
    return <Load />;
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Erro ao carregar jogos.
      </div>
    );
  }

  return (
    <div>
      {/* Filtros */}
      <Box display="flex" flexWrap="wrap" alignItems="flex-start" gap={2} mb={2}>
        <FormControl size="small" sx={{ flex: '1 1 200px', minWidth: 180 }}>
          <InputLabel id="jogos-filtro-torneio-label">Torneio</InputLabel>
          <Select
            labelId="jogos-filtro-torneio-label"
            value={filtroTorneioId}
            label="Torneio"
            onChange={(e: SelectChangeEvent) => handleFilterChange(setFiltroTorneioId, e.target.value)}
          >
            <MenuItem value=""><em>Todos</em></MenuItem>
            {torneiosUnicos.map(t => (
              <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: '1 1 160px', minWidth: 150 }}>
          <InputLabel id="jogos-filtro-status-label">Status</InputLabel>
          <Select
            labelId="jogos-filtro-status-label"
            value={filtroStatus}
            label="Status"
            onChange={(e: SelectChangeEvent) => handleFilterChange(setFiltroStatus, e.target.value)}
          >
            <MenuItem value=""><em>Todos</em></MenuItem>
            {statusOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Data início"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={filtroDataInicio}
          onChange={(e) => handleFilterChange(setFiltroDataInicio, e.target.value)}
          error={hasDateError}
          helperText={hasDateError ? 'Data início maior que data fim' : ' '}
          sx={{ flex: '1 1 160px', minWidth: 150 }}
        />

        <TextField
          label="Data fim"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={filtroDataFim}
          onChange={(e) => handleFilterChange(setFiltroDataFim, e.target.value)}
          error={hasDateError}
          helperText=" "
          sx={{ flex: '1 1 160px', minWidth: 150 }}
        />

        {temFiltroAtivo && (
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={handleLimparFiltros}
            sx={{ alignSelf: 'center' }}
          >
            Limpar
          </Button>
        )}
      </Box>

      {/* Lista de jogos */}
      {allItems.length === 0 ? (
        <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
          <div className="card-body text-center">
            <p className="mb-0">
              {temFiltroAtivo
                ? 'Nenhum jogo encontrado com os filtros selecionados.'
                : 'Nenhum jogo encontrado.'}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="row g-3">
            {items.map((partida) => (
              <div key={partida.id} className="col-12 col-lg-6">
                <MatchCard partida={partida} />
              </div>
            ))}
          </div>
          <div className="mt-3">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AtletaJogosTab;
